/**
=========================================================
* Material Dashboard 2 React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from "@mui/material/FormControlLabel";
import ButtonGroup from "@mui/material/ButtonGroup";
import CircularProgress from '@mui/material/CircularProgress';


// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";

import CustomAlert from "components/CustomAlert";


// Data
import { useEffect, useState } from "react";
import { RoleAPI } from "api/RoleAPI";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useMaterialUIController, setLoading } from "context";
import PermissionSkeleton from "components/PermissionSkeleton";
import Skeleton from "@mui/material/Skeleton";
import { yupResolver } from "@hookform/resolvers/yup";
import { useParams } from "react-router-dom";
import * as yup from 'yup'



export default function CreateEditRole() {
  const [controller, dispatch] = useMaterialUIController();
  const [permissions, setPermissions] = useState('');
  const [alert, setAlert] = useState(false);
  const [alertContent, setAlertContent] = useState('');
  const [alertColor, setAlertColor] = useState('info');
  const navigate = useNavigate();
  const { loading } = controller;

  const { id } = useParams();
  const isCreateMode = !id;

  const formSchema = yup.object().shape({
    name: yup.string().required('El campo es requerido').max(30, 'El nombre no debe tener más de 30 caracteres'),
  })

  const { handleSubmit, control, register, watch, setValue, getValues, formState: { errors, isSubmitting }, } = useForm({ resolver: yupResolver(formSchema), });

  const handleAlert = (content, color) => {
    setAlertContent(content);
    setAlertColor(color);
    setAlert(true)
  }

  const handleAlertClose = () => setAlert(false);

  const onSubmit = (data) => {
    return isCreateMode
      ? createRole(data)
      : updateRole(id, data);
  }

  const createRole = (data) => {
    return RoleAPI.store(data)
      .then(response => {
        navigate('/roles', { state: response.message });
      }).catch(error => {
        if (error.response) {
          handleAlert(error.response.data.message, 'error')
          window.scrollTo(0, 0);
        }
      });
  }

  const updateRole = (id, data) => {
    return RoleAPI.update(id, data)
      .then(response => {
        navigate('/roles', { state: response.message });
      }).catch(error => {
        if (error.response) {
          handleAlert(error.response.data.message, 'error')
          window.scrollTo(0, 0);
        }
      });
  }

  const getPermissions = (signal) => {
    return RoleAPI.listPermissions(signal)
      .then(response => {
        setPermissions(response.permissions)
      }).catch(error => {
        navigate('/not-found');
      }).finally(() => {
        setLoading(dispatch, false)
      });
  }

  const getRoleAndPermissions = (id, signal) => {
    return Promise.all([RoleAPI.show(id, signal), RoleAPI.listPermissions(signal)])
      .then(function ([response, permissions]) {
        setValue('name', response.role.role)
        setValue('permissions', response.role.permissions)
        setPermissions(permissions.permissions)
      })
      .catch(error => {
        navigate('/not-found');
      }).finally(() => {
        setLoading(dispatch, false)
      });
  }

  useEffect(() => {

    const abortController = new AbortController();
    const signal = abortController.signal
    setLoading(dispatch, true)

    !isCreateMode ? getRoleAndPermissions(id, signal) : getPermissions(signal)

    return () => {
      abortController.abort();
    };

  }, []);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pb={3}>
        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={12} lg={12}>
            <MDBox mb={4} px={2}>
              <CustomAlert severity={alertColor} message={alertContent} open={alert} onClose={handleAlertClose}></CustomAlert>
            </MDBox>
            <Card>
              <MDBox p={2} component="form" role="form" onSubmit={handleSubmit(onSubmit)}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <MDBox p={2} lineHeight={0}>
                      <MDTypography pb={2} variant="h6">Nombre del Rol</MDTypography>
                      {loading ? <Skeleton variant="text" height={50}></Skeleton> :
                        <Controller
                          control={control}
                          name="name"
                          render={({ field: { onChange, value } }) => (
                            <MDInput onChange={onChange} value={value || ''} label="Rol" variant="standard" fullWidth />
                          )}
                        />
                      }
                      {errors.name && <MDTypography variant="button" color="error">{errors.name?.message}</MDTypography>}
                    </MDBox>
                  </Grid>
                  <Grid item xs={12} sm={12}>
                    <MDBox p={2} lineHeight={0}>
                      <MDTypography variant="h5">Permisos</MDTypography>
                      <MDTypography variant="button" color="text" fontWeight="regular">
                        Lista de permisos
                      </MDTypography>
                    </MDBox>
                  </Grid>
                  {loading ? <PermissionSkeleton></PermissionSkeleton> : (
                    permissions && Object.keys(permissions).map((key) => (
                      <Grid item xs={12} sm={4} key={key}>
                        <MDBox p={2} lineHeight={0}>
                          <MDTypography variant="h6">{key}</MDTypography>
                          {Object.values(permissions[key]).map((permission) => (
                            <FormControlLabel
                              control={<Checkbox value={permission[0]['name']} {...register('permissions')}
                                checked={getValues("permissions") && watch("permissions").includes(permission[0]['name'])} />}
                              label={<MDTypography variant="button" color="text">{permission[0]['description']}</MDTypography>}
                              key={permission[0]['id']}
                              fontSize="10"
                            />
                          ))}
                        </MDBox>
                      </Grid>
                    ))
                  )}
                </Grid>
                <MDBox sx={{ display: 'flex', justifyContent: 'flex-end' }} p={5}>
                  <ButtonGroup>
                    <MDButton variant="gradient" type="submit" color="secondary" onClick={() => navigate('/roles')} >
                      Cancelar
                    </MDButton>
                    <MDButton variant="gradient" type="submit" color="info" disabled={isSubmitting}>
                      {isSubmitting ? <CircularProgress size={18} color="white" /> : 'Guardar'}
                    </MDButton>
                  </ButtonGroup>
                </MDBox>
              </MDBox>
            </Card>
            <Card></Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout >
  );
}

